import React, { useEffect } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { platformSettingsService } from '../../../services/central-api/platform-settings';
import { useAuth } from '../../../context/Auth';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import { useForm, useWatch } from 'react-hook-form';
import CustomButton from '../../../components/CustomButton';
import Toast from 'react-native-toast-message';
import FormInput from '../../../components/FormInput';
import MyAppText from '../../../components/MyAppText';
import { useStyles } from 'react-native-unistyles';

interface LoginAttemptLimitsForm {
    allowedLoginAttempts: number;
    blockedLoginWaitTime: number;
}

export default function LoginAttemptLimits() {
    const { user: authenticatedUser } = useAuth();
    const { theme } = useStyles();

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['platformSettingsLoginAttemptLimits'],
        queryFn: () => platformSettingsService.getLoginAttemptLimits(),
        enabled: !!authenticatedUser && isFocused,
    });

    const { data, refetch } = queryResult;

    const defaultEmptyValues: LoginAttemptLimitsForm = {
        allowedLoginAttempts: 0,
        blockedLoginWaitTime: 0
    };

    const form = useForm<LoginAttemptLimitsForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const allowedLoginAttempts = useWatch({ control: form.control, name: 'allowedLoginAttempts' });

    useEffect(() => {
        if (!data) {
            return;
        }
        form.reset(data);
    }, [data]);

    async function saveLoginAttemptLimits() {
        try {
            const data = form.getValues();
            await platformSettingsService.updateLoginAttemptLimits({
                allowedLoginAttempts: data.allowedLoginAttempts,
                blockedLoginWaitTime: data.allowedLoginAttempts != 0 ? data.blockedLoginWaitTime : 0
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        if (allowedLoginAttempts == 0) {
            form.setValue('blockedLoginWaitTime', 0, { shouldValidate: true });
        }
        form.trigger('blockedLoginWaitTime');
    }, [allowedLoginAttempts]);

    return (
        <View style={{ flex: 1 }}>
            <View style={{ flex: 1, gap: 20 }}>
                <FormInput
                    label={translate('allowedLoginAttempts')}
                    placeholder=''
                    form={form}
                    fieldName='allowedLoginAttempts'
                    mode='NUMBER'
                    hintText={<View style={{ flexDirection: 'column', zIndex: 9999 }}>
                        <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('allowedLoginAttemptsHint')}</MyAppText>
                    </View>}
                />
                <FormInput
                    label={translate('blockedLoginWaitTime')}
                    placeholder=''
                    form={form}
                    fieldName='blockedLoginWaitTime'
                    disabled={allowedLoginAttempts == 0}
                    required={allowedLoginAttempts != 0}
                    customValidations={{
                        validate: (val: string) => {
                            if (allowedLoginAttempts == 0) {
                                return true;
                            }
                            if (val == '0') {
                                return translate('valueNeedsToBeBiggerThanZero');
                            }
                            return true;
                        }
                    }}
                    mode='NUMBER'
                    hintText={<View style={{ flexDirection: 'column', zIndex: 9999 }}>
                        <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('blockedLoginWaitTimeHint')}</MyAppText>
                    </View>}
                />
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                <CustomButton
                    onPress={saveLoginAttemptLimits}
                    disabled={!form.formState.isValid || form.formState.isLoading}
                    text={translate('save')}
                />
            </View>
        </View>);
}
